import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Row, Col, Card, CardBody, Label, FormGroup, Button } from "reactstrap";
import { useGebruikerContext } from "../../Context/GebruikerContext";
import { ReactComponent as LocationIcon } from "../../../../../assets/utils/images/svgs/maps-icon.svg";
import { useSaveAddress, useValidateAddress } from "Api/Coordinator/Gebruikers/Algemeen";

const Address = (props) => {
  const { userDetails: data, disable } = useGebruikerContext();
  const [addressValid, setAddressValid] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [apiError, setApiError] = useState('');
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [confirmedAddressText, setConfirmedAddressText] = useState("");
  const [validatedData, setValidatedData] = useState(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const { trigger: validateAddress } = useValidateAddress();
  const { trigger: saveAddress } = useSaveAddress();


  useEffect(() => {

    if (!data) return;
    if (addressValid || addressConfirmed) return;
    if (data?.bIsAddressVerified === 1) {
      setAddressValid(true);
      setAddressConfirmed(true);
      setApiError("");
    } else if (data?.bIsAddressVerified === 0) {
      setAddressValid(false);
      setAddressConfirmed(false);
      setApiError("Adres niet geverifieerd");
    }
  }, [data?.bIsAddressVerified]);


  const validationSchema = Yup.object().shape({
    tAddress: Yup.string().required('Adres is verplicht'),
    vPostCode: Yup.string().required('Postcode is verplicht'),
    vCity: Yup.string().required('Plaats is verplicht'),
    vCountry: Yup.string().required('Land is verplicht'),
  });


  const handleAddressChange = () => {
    setAddressValid(false);
    setAddressConfirmed(false);
    setValidationError('');
    setApiError('');
    setConfirmedAddressText(null);
  };


  const confirmAddress = useCallback(async (vals, formik) => {
    try {
      const payload = {
        iAdminId: data?.iAdminId,
        tAddress: validatedData.tAddress,
        vPostCode: validatedData.vPostcode,
        vCity: validatedData.vCity,
        vCountry: vals.vCountry
      };

      const response = await saveAddress({
        data: payload,
      });

      if (response.status === true) {
        setAddressConfirmed(true);
        setAddressValid(true);
        setConfirmedAddressText(response.data);
        formik.setFieldValue("addressDisabled", true);
      } else {
        setApiError("Adres kon niet worden bevestigd.");
      }
    } catch {
      setApiError("Adresbevestiging mislukt.");
    }
  }, [validatedData, data?.iAdminId, saveAddress]);

  const cities = [
    { key: "", value: "--- Kies land ---" },
    { key: "NL", value: "Nederland" },
    { key: "BE", value: "België" },
    { key: "DE", value: "Duitsland" }
  ];

  return (
    <Card className="main-card card-fixed-height">
      <CardBody className="d-flex flex-column">
        <h5 className="mb-4">Adresgegevens</h5>

        <Formik
          enableReinitialize
          initialValues={{
            tAddress: data?.tAddress || "",
            vPostCode: data?.vPostCode || "",
            vCity: data?.vCity || "",
            vCountry: data?.vCountry || "",
          }}
          validationSchema={validationSchema}
          onSubmit={() => { }}
        >
          {(formik) => (
            <Form>
              <FormGroup
                className={`form-element ${formik.errors.tAddress && formik.touched.tAddress ? "error" : ""
                  }`}
              >
                <Label for="tAddress">Adres</Label>
                <div className="w-100">
                  <Field
                    name="tAddress"
                    type="text"
                    className={`form-control ${formik.errors.tAddress && formik.touched.tAddress ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                    disabled={disable}
                  />
                  <ErrorMessage name="tAddress" component="small" className="invalid-feedback" />
                </div>
              </FormGroup>


              <FormGroup className="form-element">
                <Label>Postcode</Label>
                <div className="w-100">

                  <Field
                    name="vPostCode"
                    className={`form-control ${formik.errors.vPostCode && formik.touched.vPostCode ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                    disabled={disable}
                  />
                  <ErrorMessage name="vPostCode" component="small" className="invalid-feedback" />
                </div>
              </FormGroup>
              <FormGroup className="form-element">
                <Label>Woonplaats</Label>
                <div className="w-100">
                  <Field
                    name="vCity"
                    className={`form-control ${formik.errors.vCity && formik.touched.vCity ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                    disabled={disable}
                  />
                  <ErrorMessage name="vCity" component="small" className="invalid-feedback" />
                </div>

              </FormGroup>
              <FormGroup
                className={`form-element ${formik.errors.vCountry && formik.touched.vCountry ? "error" : ""
                  }`}
              >
                <Label for="vCountry">Land</Label>
                <div className="w-100">

                  <Field
                    as="select"
                    name="vCountry"
                    className={`form-select ${formik.errors.vCountry && formik.touched.vCountry ? "is-invalid" : ""
                      }`}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleAddressChange();
                    }}
                    disabled={disable}
                  >
                    {cities.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.value}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="vCountry" component="small" className="invalid-feedback" />
                </div>
              </FormGroup>

              <Row >
                <Col sm={4}></Col>
                <Col sm={12}>
                  {confirmedAddressText?.formattedAddress && (
                    <p className=" fw-semibold small mb-2 mt-n2 d-flex justify-content-end">
                      Google Maps adres: {confirmedAddressText.formattedAddress}
                    </p>
                  )}


                  {!addressConfirmed && addressValid && (
                    <p className="small fw-semibold mt-1 d-flex justify-content-end">
                      Adres gevalideerd voor automatische afstand berekening
                    </p>
                  )}

                  {validationError && (
                    <p className="text-danger small fw-semibold mt-1 d-flex justify-content-end">{validationError}</p>
                  )}

                  {apiError && (
                    <p className="text-danger small fw-semibold mt-1 d-flex justify-content-end">{apiError}</p>
                  )}
                  <div className="d-flex justify-content-end">

                    {!addressValid && (
                      <Button
                        outline
                        className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center mt-2"
                        type="button"
                        disabled={disable}

                        onClick={async () => {
                          const vals = formik.values;

                          if (!vals.tAddress || !vals.vPostCode || !vals.vCity || !vals.vCountry) {
                            setValidationError("Vul alle adresvelden in.");
                            return;
                          }

                          setValidationError("");
                          setApiError("");
                          setConfirmedAddressText(null);

                          try {
                            const result = await validateAddress({
                              data: {
                                iAdminId: data.iAdminId,
                                tAddress: vals.tAddress,
                                vPostCode: vals.vPostCode,
                                vCity: vals.vCity,
                                vCountry: vals.vCountry,
                              },
                            });

                            if (result.status) {
                              setAddressValid(true);
                              setConfirmedAddressText(result.data);

                              formik.setFieldValue("tAddress", result.data.tAddress);
                              formik.setFieldValue("vCity", result.data.vCity);
                              formik.setFieldValue("vPostCode", result.data.vPostcode)

                              setValidatedData(result.data);
                            } else {
                              setApiError('Google adres verificatie niet mogelijk, controleer het adres ');
                            }
                          } catch {
                            setApiError("Validatie mislukt.");
                          }
                        }}
                      >
                        {/* <LocationIcon style={{ width: 18, height: 18 }} /> */}
                        Opslaan
                      </Button>
                    )}


                    {addressValid && !addressConfirmed && (
                      <Button
                        outline
                        disabled={isButtonDisabled}
                        className="btn-outline-primary-blue btn-color-style py-2 px-3 d-flex gap-2 align-items-center mt-2"
                        type="button"
                        onClick={async () => {
                          setIsButtonDisabled(true);

                          try {
                            await confirmAddress(formik.values, formik);
                          } finally {
                            setIsButtonDisabled(false);
                          }
                        }}
                      >
                        Toepassen
                      </Button>
                    )}
                  </div>

                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </CardBody>
    </Card>

  );
};

export default Address;
